import {right} from "@popperjs/core";
import {AxiosError} from "axios";
import {
    SecurityPass as TrustedIcon,
    SecurityPass as TrustUnknownIcon,
    WarningCircle as NotTrustedIcon
} from "iconoir-react";
import {useEffect, useState} from 'react'
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Tooltip from "react-bootstrap/Tooltip";
import {useTranslation} from "react-i18next";
import {ApiConfig} from "../api/admin/ApiConfig";
import {CRLApi, CRLValidationResult, X509CRLDetails} from "../api/admin/generated";
import {Truncate} from "../common/Truncate";
import {useSpinnerStatusContext} from "../hook/UseSpinnerStatusContext";
import {DateUtils} from "../utils/DateUtils";
import {logger} from "../utils/Logger";

/*
 * REST api for accessing the back-end
 */
const crlApi = new CRLApi(ApiConfig.config());

interface CRLColumnProps {
    value? : string
    children? : React.ReactNode
    className? : string
}

const CRLColumn = ({value, children, className} : CRLColumnProps) => {
    return (
        <td className={"user-select-all " + className}>{children}<Truncate value={value} maxLength={60}/></td>
    )
}

interface CertificateRowProps {
    crl : X509CRLDetails
    children? : React.ReactNode
}

/**
 * Represents a row in a Certificate Revocation List (CRL) table.
 *
 * @typedef {Object} CRLRow
 * @property {CertificateRowProps} crl - The CRL object containing certificate information.
 * @property {ReactNode} children - The children elements of the row.
 */
export const CRLRow = ({crl, children} : CertificateRowProps ) => {
    /**
     * The CRL validation status
     */
    const [crlValidationResult, setCrlValidationResult] = useState<CRLValidationResult>();
    /**
     * Keeps track of activities for which the spinner should be shown. If an activity is started, the counter should
     * be increased by one and after the activity
     */
    const {setSpinnerStateCounter} = useSpinnerStatusContext();
    /**
     * i18next translation hook
     */
    const {t} = useTranslation();

    useEffect(() => {
        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        crlApi.cRLControllerValidateCRL(crl.thumbprint)
            .then(response => {
                setCrlValidationResult(response.data);
            })
            .catch((err: Error | AxiosError) => {
                logger.error("Error getting CRL validation result", err);
                setCrlValidationResult(undefined);
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
            })
    }, [crl.thumbprint,
        setSpinnerStateCounter]);

    const trustIcon = () =>
    {
        if (crlValidationResult === undefined) {
            return (
                <OverlayTrigger
                    placement={right}
                    overlay={
                        <Tooltip>
                            {t("crls.trust-unknown")}
                        </Tooltip>
                    }
                >
                    <TrustUnknownIcon className="text-secondary table-icon-width table-icon-height ms-2" />
                </OverlayTrigger>
            );
        }

        if (crlValidationResult.trusted) {
            return (
                <OverlayTrigger
                    placement={right}
                    overlay={
                        <Tooltip>
                            {t("crls.trusted")}
                        </Tooltip>
                    }
                >
                    <TrustedIcon className="text-success table-icon-width table-icon-height ms-2"/>
                </OverlayTrigger>
            );
        }

        return (
            <OverlayTrigger
                placement={right}
                overlay={
                    <Tooltip>
                        {crlValidationResult?.failureMessage}
                    </Tooltip>
                }
            >
                <NotTrustedIcon className="text-danger table-icon-width table-icon-height ms-2" />
            </OverlayTrigger>
        )
    }

    return (
        <tr key={crl.thumbprint}>
            <CRLColumn className="td-w-min">
                <div className="d-flex align-items-center">
                    {children}

                    {trustIcon()}
                </div>
            </CRLColumn>
            <CRLColumn value={crl.issuerFriendly}/>
            <CRLColumn value={DateUtils.timestampToDateStringShort(crl.nextUpdate)}/>
            <CRLColumn value={DateUtils.timestampToDateStringShort(crl.thisUpdate)}/>
            <CRLColumn value={`${crl.version}`}/>
            <CRLColumn value={crl.crlNumber}/>
            <CRLColumn value={crl.deltaIndicator}/>
            <CRLColumn value={`${crl.deltaCRL}`}/>
            <CRLColumn value={crl.thumbprint}/>
            <CRLColumn value={crl.thumbprintSHA1}/>
            <CRLColumn value={crl.signatureAlgorithm}/>
            <CRLColumn value={crlValidationResult?.failureMessage} className="text-danger" />
        </tr>
    )
}